import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import { get } from 'lodash';

import AbstractFopContainer from './AbstractFopContainer';
import ExternalProductsContainer from '[theme-lib]/apps/externalProducts/containers/ExternalProductsContainer';
import FopBanner from '[theme-lib]/apps/catalogue/components/FopBanner';
import FopList from 'apps/fops/containers/FopList';
import PromoBoxInMainContainer from '../containers/PromoBoxInMainContainer';
import RegularFopElementsFactory from 'apps/fops/components/fopElementsFactories/RegularFopElementsFactory';
import ShowAllFavourites from './ShowAllFavourites';
import ShowMoreButton from './ShowMoreButton';
import { FopContainerBasketItems, FopContainerViewPreferenceShape, FopContainerVisibleFops } from '../types';

import { gtm } from 'apps/gtm';

import ExtSectionTypes from 'apps/externalProducts/ExtSectionTypes';
import SectionType from 'apps/catalogue/models/SectionType';
import { VIEW_TYPE_PREFERENCE_SHELF } from '../actions/catalogue';

const createVisibleProductsBySku = (visibleFops, productsBySku) => {
  // All FOPs returned in SSR shall be rendered (on server, there's no visibleFops)
  if (!visibleFops || !visibleFops.length) {
    return productsBySku;
  }

  // NOTE: please do not refactor to [].map() as it works much slower
  const result = {};
  for (let i = 0; i < visibleFops.length; ++i) {
    const { sku } = visibleFops[i];
    if (productsBySku[sku]) {
      result[sku] = productsBySku[sku];
    }
  }
  return result;
};

const insertBeforeSpecifiedSection = (sectionToInsert, sections, { beforeSection, clusterNumber }) => {
  let beforeSectionIndex = 0;
  if (beforeSection || clusterNumber) {
    /*
      There might be more than one sections of CLUSTER type.
      Need to add advert before the specified one by section number
     */
    if (clusterNumber > 0) {
      const cluster =
        sections.filter(section => section.attributes.type === SectionType.CLUSTER)[clusterNumber - 1];
      if (cluster) {
        beforeSectionIndex = sections.findIndex(section => section.id === cluster.id);
      }
    } else {
      beforeSectionIndex = sections.findIndex(section => section.attributes.type === beforeSection);
    }
  }

  // Add before the specified section
  if (beforeSectionIndex > 0) {
    return [
      ...sections.slice(0, beforeSectionIndex),
      sectionToInsert,
      ...sections.slice(beforeSectionIndex),
    ];
  } else {
    // Otherwise add it to the beginning
    return [sectionToInsert, ...sections];
  }
};

const addShowAllFavourites = (sections, pageParameters) => {
  const showAllFavouritesElem = [
    <ShowAllFavourites
      key="showAllFops"
      pageParameters={pageParameters}
    />,
  ];

  return sections.map(section => {
    // Limit favourites and add "Show all" button
    if (section.attributes.type === SectionType.FAVOURITES) {
      return {
        ...section,
        contentAfterFops: showAllFavouritesElem,
      };
    }

    return section;
  });
};

export default class MainFopContainer extends AbstractFopContainer {
  static propTypes = {
    afterBasketQuantityChange: PropTypes.func,
    baseName: PropTypes.string,
    basket: PropTypes.object,
    basketItems: FopContainerBasketItems,
    betaFeatures: PropTypes.array,
    dispatch: PropTypes.func.isRequired,
    extraLocationParams: PropTypes.object,
    customFopBanner: PropTypes.object,
    fopBanner: PropTypes.object,
    fopCollection: PropTypes.shape({
      sections: PropTypes.arrayOf(PropTypes.object),
      totalFops: PropTypes.number.isRequired,
      maxDisplayFops: PropTypes.number.isRequired,
    }).isRequired,
    fopViewPreference: FopContainerViewPreferenceShape,
    generalPageInfo: PropTypes.shape({
      displayOptions: PropTypes.shape({
        showLimitedFavouritesNumber: PropTypes.bool,
      }),
    }),
    gtmPageType: PropTypes.string,
    landingPage: PropTypes.object,
    outOfStockStatistics: PropTypes.arrayOf(PropTypes.shape({
      category: PropTypes.string.isRequired,
      oosRatio: PropTypes.number.isRequired,
    })),
    pageParameters: PropTypes.object.isRequired,
    productsBySku: PropTypes.object.isRequired,
    productWarnings: PropTypes.objectOf(PropTypes.array),
    quickMenuExpanded: PropTypes.object,
    routeString: PropTypes.string.isRequired,
    scrollWatcher: PropTypes.object.isRequired,
    shoppingListsModal: PropTypes.bool,
    showMoreMainFopCollectionFops: PropTypes.func.isRequired,
    subscriptionsBySku: PropTypes.object,
    visibleFops: FopContainerVisibleFops,
  };

  static reportOOSStats(outOfStockStatistics, gtmPageType) {
    gtm.trigger('gtm:oosRatio', {
      outOfStockStatistics,
      gtmPageType,
    });
  }

  componentDidMount() {
    const { outOfStockStatistics, gtmPageType } = this.props;
    MainFopContainer.reportOOSStats(outOfStockStatistics, gtmPageType);
  }

  componentDidUpdate(prevProps) {
    const shouldCheckFopsVisibility =
      (this.props.fopCollection !== prevProps.fopCollection) ||
      (this.props.fopViewPreference !== prevProps.fopViewPreference);

    if (shouldCheckFopsVisibility) {
      this.props.scrollWatcher.check({ force: true });
    }
  }

  handleShowMoreClick = () => {
    const { routeString } = this.props;

    // TODO add analytics
    this.props.showMoreMainFopCollectionFops(routeString);
  };

  organiseSections = () => {
    const { pageParameters, fopCollection: { sections } } = this.props;
    const showLimitedFavouritesNumber = get(
      this.props, 'generalPageInfo.displayOptions.showLimitedFavouritesNumber', false
    );

    let organisedSections = sections;

    // Add "show all favourites" if enabled
    if (showLimitedFavouritesNumber) {
      organisedSections = addShowAllFavourites(organisedSections, pageParameters);
    }

    // Add surrounding content sections
    organisedSections = this.addContentAroundSections(organisedSections);

    // Add custom fop section
    organisedSections = this.addAdvertSection('customFopSection', organisedSections);

    // Add fop banner section
    organisedSections = this.addAdvertSection('fopBanner', organisedSections);

    return organisedSections;
  };

  addContentAroundSections = (sections) => {
    const contentBeforeSections = {
      id: 'content-before-sections',
      attributes: { type: SectionType.CONTENT },
      contentBeforeFops: this.addPromoBoxes(),
    };

    // Currently not used, left for consistency
    const contentAfterSections = {
      id: 'content-after-sections',
      attributes: { type: SectionType.CONTENT },
      contentBeforeFops: null,
    };

    return [contentBeforeSections, ...sections, contentAfterSections];
  };

  addAdvertSection = (advertType, sections) => {
    const { [advertType]: advert, fopViewPreference } = this.props;
    const isShelfView = fopViewPreference === VIEW_TYPE_PREFERENCE_SHELF;
    const advertNo = advert && advert.docNo;

    if (isShelfView && advertNo) {
      return this.createAdvertSection(advertType, advert, sections);
    }

    return sections;
  };

  createAdvertSection = (advertType, advert, sections) => {
    const { fopViewPreference, landingPage = {} } = this.props;
    const { fops, imageWidth } = advert;
    const { docNo: parentNo, docVersionNo: parentVersionNo, docTitle: parentTitle } = landingPage;
    const isTwoFopsWidth = imageWidth === 2;

    const advertSection = {
      id: `advert-${advert.docNo}`,
      attributes: { type: SectionType.ADVERT },
      fops,
      contentBeforeFops: (
      <FopBanner
        {...advert}
        advertType={advertType}
        fopViewPreference={fopViewPreference}
        id={`FopBanner-${advert.docNo}`}
        isTwoFopsWidth={isTwoFopsWidth}
        key={`FopBanner-${advert.docNo}`}
        parentNo={parentNo}
        parentTitle={parentTitle}
        parentVersionNo={parentVersionNo}
      />
      ),
    };

    return insertBeforeSpecifiedSection(advertSection, sections, advert);
  };

  addPromoBoxes = () => {
    const { extraLocationParams, fopBanner, fopViewPreference, landingPage = {} } = this.props;
    const { promotedBoxes } = landingPage;

    const promotedBoxesContainers = [];
    if (!fopBanner || !fopBanner.docNo) {
      // Fop banner has a precedence over External Products Container
      const externalProductsKey = `ExtProducts${ExtSectionTypes.MAIN_FOP_LIST}`;

      promotedBoxesContainers.push(
        <ExternalProductsContainer
          desktopOnly
          extraLocationParams={extraLocationParams}
          fopViewPreference={fopViewPreference}
          key={externalProductsKey}
          name={ExtSectionTypes.MAIN_FOP_LIST}
        />
      );
    }

    if (promotedBoxes) {
      const numberOfColumns = Math.ceil(promotedBoxes.length / 2);

      for (let i = 0; i < numberOfColumns; i++) {
        promotedBoxesContainers.push(
          <PromoBoxInMainContainer
            key={i}
            firstElement={promotedBoxes[i]}
            secondElement={promotedBoxes[i + numberOfColumns]}
            landingPage={landingPage}
            idx={i}
            sectionLength={numberOfColumns}
          />
        );
      }
    }

    return promotedBoxesContainers;
  };

  render() {
    const {
      fopCollection: {
        totalFops,
        maxDisplayFops,
        sections,
      },
      basketItems,
      betaFeatures,
      fopViewPreference,
      gtmPageType,
      productsBySku,
      productWarnings,
      scrollWatcher,
      visibleFops,
      pageParameters,
    } = this.props;

    if (sections === undefined) {
      return null;
    }

    // filter the products to only currently visible ones, so for others placeholders are used
    const visibleProductsBySku = createVisibleProductsBySku(visibleFops, productsBySku);

    const showMoreButton = (totalFops > maxDisplayFops)
      ? <ShowMoreButton onClick={this.handleShowMoreClick} />
      : null;

    const regularFopElementsFactory = new RegularFopElementsFactory({
      basketItems,
      betaFeatures,
      onQuantitySubmit: this.onQuantitySubmit,
      productsBySku: visibleProductsBySku,
      productWarnings,
      viewTypePreference: fopViewPreference,
    });

    return (
      <Fragment>
        <FopList
          sections={this.organiseSections()}
          pageParameters={pageParameters}
          sectionRibbonDisplayed
          maxDisplayFops={maxDisplayFops}
          totalFops={totalFops}
          productsBySku={visibleProductsBySku}
          productWarnings={productWarnings}
          registerFopRef={scrollWatcher.registerFopRef}
          fopElementsFactory={regularFopElementsFactory}
          gtmPageType={gtmPageType}
        />
        {showMoreButton}
      </Fragment>
    );
  }
}
